import { useGSAP } from '@gsap/react';
import classNames from 'classnames';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import TextPlugin from 'gsap/dist/TextPlugin';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { SplitChar } from '../../splitchar/splitchar';
import styles from './b2b-integrations.module.scss';

interface Brand {
    name: string;
    logo: string;
    width: number;
    height: number;
    link?: string;
}

export interface B2BIntegrationsProps {
    title: string;
    sections: {
        title: string;
        brands: Brand[];
    }[];
}

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(TextPlugin);

const options: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 10,
    allowTouchMove: true,
    noSwiping: false,
    freeMode: false,
    loop: false,
    navigation: false,
    pagination: false,
    breakpoints: {
        992: {
            enabled: false,
        },
    },
};

const B2BIntegrations: FC<B2BIntegrationsProps> = (props) => {
    const { title, sections = [] } = props;

    const [render, setRender] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTimeout(() => {
            setRender(true);
        }, 1000);
    }, []);

    useGSAP(
        () => {
            if (!render) return;

            const selector = gsap.utils.selector(containerRef.current);
            const sections = gsap.utils.toArray<HTMLElement>(selector(`.${styles.section}`));

            let mm = gsap.matchMedia();

            mm.add('(min-width: 993px)', (context) => {
                gsap.timeline({
                    scrollTrigger: {
                        trigger: containerRef.current,
                        pinnedContainer: containerRef.current,
                        pin: true,
                        start: 'center center',
                        end: '+=100%',
                        scrub: 1,
                    },
                });

                let masterTl = gsap.timeline({
                    scrollTrigger: {
                        invalidateOnRefresh: true,
                        trigger: containerRef.current,
                        start: 'top center+=40%',
                        end: 'bottom top',
                        scrub: true,
                    },
                });

                gsap.set(sections, { autoAlpha: 0 });

                for (let i = 0; i < sections.length; i++) {
                    const section = sections[i];

                    const title = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(section)(`.${styles.sectionTitle}`))[0];

                    const prvSection = sections[i - 1];
                    if (prvSection) {
                        const prvTitle = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(prvSection)(`.${styles.sectionTitle}`))[0];
                        const tlOut = animateSection(prvSection, prvTitle, 'out');
                        masterTl.add(tlOut);
                    }

                    const tlIn = animateSection(section, title, 'in', i === 0 ? '+=1' : '<');
                    masterTl.to(section, { xPercent: -100 * i, x: 0 }, '<');
                    masterTl.add(tlIn, '-=0.4');
                }
            });
        },
        { scope: containerRef, dependencies: [sections, render] }
    );

    const animateSection = (section: HTMLElement, title: HTMLElement, animationType: 'in' | 'out', position?: gsap.Position) => {
        const sectionSelector = gsap.utils.selector(section);
        const contentItems = gsap.utils.toArray<HTMLDivElement>(sectionSelector(`.${styles.sectionRightContent} .${styles.logoTile}`));
        const titleSplits = gsap.utils.toArray<HTMLDivElement>(gsap.utils.selector(title)(`span`));

        const tl = gsap.timeline({});

        if (animationType === 'in') {
            tl.to(section, { autoAlpha: 1 }, position);
            tl.fromTo(
                [titleSplits, contentItems],
                {
                    autoAlpha: 0,
                },
                {
                    duration: 1,
                    autoAlpha: 1,
                    stagger: {
                        from: 'start',
                        each: 0.02,
                    },
                },
                '>-=0.5'
            );
        } else {
            tl.to(
                [titleSplits, contentItems],
                {
                    duration: 0.5,
                    autoAlpha: 0,
                    stagger: {
                        from: 'start',
                        each: 0.005,
                    },
                },
                '+=1'
            );
        }

        return tl;
    };

    const _renderCarousel = (brands: Brand[]) => {
        if (brands.length > 3) {
            let halfwayThrough = Math.floor(brands.length / 2);

            let arrayFirstHalf = brands.slice(0, halfwayThrough);
            let arraySecondHalf = brands.slice(halfwayThrough, brands.length);

            return arrayFirstHalf.map((brand, index) => (
                <SwiperSlide key={brand.name}>
                    <div className={styles.logoGroup}>
                        <div className={styles.logoTile}>
                            <div className={styles.logo} style={{ aspectRatio: `${(brand.width || 1) / (brand.height || 1)}`, maxWidth: brand.width, height: brand.height }}>
                                <Image
                                    src={brand.logo || ''}
                                    alt={brand.name}
                                    style={{ objectFit: 'contain', objectPosition: 'center' }}
                                    sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                    fill={true}
                                    loading={'eager'}
                                    loader={({ src, width, quality }) => {
                                        return `${src}?width=${width}&quality=${quality ?? 75}`;
                                    }}
                                />
                            </div>
                        </div>

                        {arraySecondHalf[index] ? (
                            <div className={styles.logoTile}>
                                <div
                                    className={styles.logo}
                                    style={{
                                        aspectRatio: `${(arraySecondHalf[index].width || 1) / (arraySecondHalf[index].height || 1)}`,
                                        maxWidth: arraySecondHalf[index].width,
                                        height: arraySecondHalf[index].height,
                                    }}
                                >
                                    <Image
                                        src={arraySecondHalf[index].logo || ''}
                                        alt={arraySecondHalf[index].name}
                                        style={{ objectFit: 'contain', objectPosition: 'center' }}
                                        sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                        fill={true}
                                        loading={'eager'}
                                        loader={({ src, width, quality }) => {
                                            return `${src}?width=${width}&quality=${quality ?? 75}`;
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                </SwiperSlide>
            ));
        }

        return brands.map((brand) => (
            <SwiperSlide key={brand.name}>
                <div className={styles.logoTile}>
                    <div className={styles.logo} style={{ aspectRatio: `${(brand.width || 1) / (brand.height || 1)}`, maxWidth: brand.width, height: brand.height }}>
                        <Image
                            src={brand.logo || ''}
                            alt={brand.name}
                            style={{ objectFit: 'contain', objectPosition: 'center' }}
                            sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                            fill={true}
                            loading={'eager'}
                            loader={({ src, width, quality }) => {
                                return `${src}?width=${width}&quality=${quality ?? 75}`;
                            }}
                        />
                    </div>
                </div>
            </SwiperSlide>
        ));
    };

    return (
        <div ref={containerRef} className={styles.container}>
            <div className={styles.content}>
                <SplitChar text={title} className={classNames(styles.title, styles.desktopHide)} />
                {sections.map((section, index) => {
                    const { title: sectionTitle = '', brands = [] } = section;
                    return (
                        <section key={`${sectionTitle}_${index}`} className={styles.section}>
                            <div className={styles.sectionLeft}>
                                <SplitChar text={title} className={classNames(styles.title, styles.mobileHide)} />
                                <SplitChar text={sectionTitle} className={styles.sectionTitle} />
                            </div>
                            <div className={styles.sectionRight}>
                                <div className={styles.sectionRightContent}>
                                    <div className={styles.mobileHide}>
                                        <div className={styles.sectionGrid}>
                                            {brands.map((brand) => (
                                                <div key={brand.name} className={styles.logoTile}>
                                                    <div
                                                        className={styles.logo}
                                                        style={{ aspectRatio: `${(brand.width || 1) / (brand.height || 1)}`, maxWidth: brand.width, height: brand.height }}
                                                    >
                                                        <Image
                                                            src={brand.logo || ''}
                                                            alt={brand.name}
                                                            style={{ objectFit: 'contain', objectPosition: 'center' }}
                                                            sizes="(max-width: 638px) 98vw,  (max-width: 998px) 43vw, 46vw"
                                                            fill={true}
                                                            loading={'eager'}
                                                            loader={({ src, width, quality }) => {
                                                                return `${src}?width=${width}&quality=${quality ?? 75}`;
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={styles.desktopHide}>
                                        <Swiper {...options}>{_renderCarousel(brands)}</Swiper>
                                    </div>
                                </div>
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

export default B2BIntegrations;
